import type { DataRow } from '../../lib/types.js';
import { fmtInt, fmtMoney, fmtPct, mmdd } from '../../lib/format.js';
import { hiddenColumnsNote, visibleColumns } from '../../lib/visibleColumns.js';
import { useIsPhone } from '../../lib/use-media.js';
import { Excluded, tdClass, type Col } from '../table-bits.js';

/**
 * The Daily breakdown table, exactly as it was on the Dashboard before it moved
 * to the Daily page (owner correction 2026-10-27: the table with the daily stats
 * per ID, one row per account, feed and geo, grouped by day with each day's
 * revenue and attributed cost). Restored from the Dashboard's source unchanged.
 */
export function DailyTable(props: {
  rows: DataRow[];
  total: number;
  hideEmpty: boolean;
  showCosts: boolean;
  daily: { day: string; revenue: number; cost: number | null }[];
  partialDay: string | null;
  csvHref: string;
  provDisp: (p: string, l?: string | null) => string;
  rowSource: (r: {
    accountId: string;
    provider: string;
    providerLabel?: string | null | undefined;
  }) => string;
  onMore: () => void;
}) {
  // Phone (2026-09-14 mobile design): the same rows as a list grouped by
  // day - identity on one line, revenue beside it, the figures under - since
  // a 14-column table only ever shows its first three on a phone.
  const phone = useIsPhone();
  const rows = props.hideEmpty
    ? props.rows.filter((r) => r.searches > 0 || r.payout > 0 || r.paidClicks > 0)
    : props.rows;
  const byDay = new Map<string, DataRow[]>();
  for (const r of rows) {
    const list = byDay.get(r.day) ?? [];
    list.push(r);
    byDay.set(r.day, list);
  }
  const dayCost = new Map(props.daily.map((d) => [d.day, d.cost]));

  // Money is summed in USD (payoutUsd), never native payout: the day headers
  // always were, and a footer summing 1,000 JPY as $1,000 next to a $10 day
  // header is the wrong table (R9). An unrated non-USD row contributes 0 and
  // is flagged on its own cell.
  const totals = rows.reduce(
    (t, r) => ({
      requests: t.requests + r.requests,
      searches: t.searches + r.searches,
      monetized: t.monetized + r.monetizedSearches,
      clicks: t.clicks + r.paidClicks,
      payoutUsd: t.payoutUsd + r.payoutUsd,
    }),
    { requests: 0, searches: 0, monetized: 0, clicks: 0, payoutUsd: 0 },
  );

  // Date, Provider, Type and Rep are the row's identity and never hide;
  // Feed, Geo and every figure hide when empty on every row shown.
  const cols: Col<DataRow>[] = [
    {
      key: 'Date',
      head: 'l',
      fixed: true,
      get: (r) => r.day,
      cellClass: 'l dim',
      cell: (r) => mmdd(r.day),
    },
    {
      key: 'Provider',
      head: 'l',
      fixed: true,
      get: (r) => r.provider,
      cellClass: 'l',
      cell: (r) => props.rowSource(r),
    },
    {
      key: 'Type',
      head: 'l',
      fixed: true,
      get: (r) => r.type,
      cellClass: 'l',
      cell: (r) => r.type,
    },
    { key: 'Rep', head: 'l', fixed: true, get: (r) => r.rep, cellClass: 'l', cell: (r) => r.rep },
    { key: 'Feed', head: 'l', get: (r) => r.feedId, cellClass: 'l', cell: (r) => r.feedId ?? '—' },
    { key: 'Geo', head: 'l', get: (r) => r.country, cellClass: 'l', cell: (r) => r.country ?? '—' },
    {
      key: 'Requests',
      get: (r) => r.requests,
      cell: (r) => fmtInt(r.requests),
      foot: fmtInt(totals.requests),
    },
    {
      key: 'Searches',
      get: (r) => r.searches,
      cell: (r) => fmtInt(r.searches),
      foot: fmtInt(totals.searches),
    },
    {
      key: 'Monetized',
      get: (r) => r.monetizedSearches,
      cell: (r) => fmtInt(r.monetizedSearches),
      foot: fmtInt(totals.monetized),
    },
    {
      key: 'Coverage',
      get: (r) => r.coverage,
      cellClass: 'dim',
      cell: (r) =>
        r.coverage != null
          ? r.coverage.toFixed(1) + '%' + (r.coverageSource === 'calculated' ? '*' : '')
          : '—',
      foot:
        totals.searches > 0 ? ((100 * totals.monetized) / totals.searches).toFixed(1) + '%' : '—',
      footClass: 'dim',
    },
    {
      // Only three providers report one, so for most sources every row is
      // empty and the all-empty rule hides the column (lib/visibleColumns.ts).
      // Shown exactly as stored: the scale each provider uses is not known, so
      // rescaling it would be inventing a number.
      key: 'TQ',
      get: (r) => r.tq,
      cellClass: 'dim',
      title: () =>
        "the provider's own traffic-quality figure, as it reports it — not rescaled, and not comparable between providers",
      cell: (r) => (r.tq != null ? r.tq.toFixed(2) : '—'),
      // A rate has no total: the footer would be a number nobody could act on.
      foot: '—',
      footClass: 'dim',
    },
    {
      key: 'Clicks',
      get: (r) => r.paidClicks,
      cell: (r) => fmtInt(r.paidClicks),
      foot: fmtInt(totals.clicks),
    },
    {
      key: 'CPC',
      get: (r) => (r.paidClicks > 0 ? r.payoutUsd / r.paidClicks : null),
      cellClass: 'dim',
      cell: (r) => (r.paidClicks > 0 ? fmtMoney(r.payoutUsd / r.paidClicks) : '—'),
      foot: totals.clicks > 0 ? fmtMoney(totals.payoutUsd / totals.clicks) : '—',
      footClass: 'dim',
    },
    {
      key: 'CTR',
      get: (r) => (r.monetizedSearches > 0 ? (100 * r.paidClicks) / r.monetizedSearches : null),
      cellClass: 'dim',
      cell: (r) =>
        r.monetizedSearches > 0 ? fmtPct((100 * r.paidClicks) / r.monetizedSearches) : '—',
      foot: totals.monetized > 0 ? fmtPct((100 * totals.clicks) / totals.monetized) : '—',
      footClass: 'dim',
    },
    {
      key: 'Revenue',
      // An excluded row has something to show (its currency and the reason), so it keeps the column.
      get: (r) => (r.fxMissing ? 'excluded' : r.payoutUsd),
      cellClass: 'pay',
      title: (r) =>
        r.currency !== 'USD'
          ? `${r.payout.toFixed(4)} ${r.currency}${r.fxMissing ? ' — excluded, no USD rate yet' : ''}`
          : undefined,
      cell: (r) => (
        <>
          {fmtMoney(r.payoutUsd)}
          {r.currency !== 'USD' ? <span className="dim"> {r.currency}</span> : null}
          {r.fxMissing ? <Excluded n={1} /> : null}
        </>
      ),
      foot: fmtMoney(totals.payoutUsd),
      footClass: 'pay',
    },
  ];
  const { shown, hidden } = visibleColumns(rows, cols);
  // The footer label spans the identity columns (the ones without a total).
  const footLead = shown.filter((c) => c.foot === undefined).length;
  // The phone list hides what the table hides (a figure that is zero on every
  // row shown), so the note about hidden columns stays true for both.
  const hiddenKeys = new Set(hidden);
  const figures = (r: DataRow): string =>
    [
      hiddenKeys.has('Searches') ? null : `${fmtInt(r.searches)} searches`,
      hiddenKeys.has('Monetized')
        ? null
        : `${fmtInt(r.monetizedSearches)} monetized` +
          (hiddenKeys.has('Coverage') || r.coverage == null
            ? ''
            : ` (${r.coverage.toFixed(1)}%${r.coverageSource === 'calculated' ? '*' : ''})`),
      hiddenKeys.has('Clicks') ? null : `${fmtInt(r.paidClicks)} clicks`,
      hiddenKeys.has('CPC') || r.paidClicks <= 0
        ? null
        : `CPC ${fmtMoney(r.payoutUsd / r.paidClicks)}`,
      hiddenKeys.has('CTR') || r.monetizedSearches <= 0
        ? null
        : `CTR ${fmtPct((100 * r.paidClicks) / r.monetizedSearches)}`,
      hiddenKeys.has('Requests') ? null : `${fmtInt(r.requests)} requests`,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <div className="table-card">
      <div className="table-head">
        <h2>Daily breakdown</h2>
        <span className="count">
          {rows.length} of {props.total} rows
        </span>
        <a
          className="ghost small"
          href={props.csvHref}
          download
          title="Downloads every row in the current filtered range as CSV — not just the rows shown here."
        >
          Export CSV
        </a>
      </div>
      {phone ? (
        <div className="daily-cards">
          {[...byDay.entries()].map(([day, dayRows]) => {
            const dRev = dayRows.reduce((a, r) => a + r.payoutUsd, 0);
            const cost = dayCost.get(day);
            return (
              <div className="day-group" key={day}>
                <div className="day-head">
                  <span className="day">
                    {mmdd(day)}
                    {day === props.partialDay ? ' · partial' : ''}
                  </span>
                  <span className="day-sum">
                    {fmtMoney(dRev)}
                    {props.showCosts && cost != null
                      ? ` · cost ${fmtMoney(cost)} · net ${fmtMoney(dRev - cost)}`
                      : ''}
                  </span>
                </div>
                {dayRows.map((r, i) => (
                  <div className="day-row" key={`${day}-${i}`}>
                    <div className="dr-head">
                      <span className="dr-id">
                        {props.rowSource(r)} · {r.type} · {r.rep}
                        {r.feedId && !hiddenKeys.has('Feed') ? ` · Feed ${r.feedId}` : ''}
                        {r.country && !hiddenKeys.has('Geo') ? ` · ${r.country}` : ''}
                      </span>
                      <span
                        className="dr-rev"
                        title={
                          r.currency !== 'USD'
                            ? `${r.payout.toFixed(4)} ${r.currency}${r.fxMissing ? ' — excluded, no USD rate yet' : ''}`
                            : undefined
                        }
                      >
                        {fmtMoney(r.payoutUsd)}
                        {r.currency !== 'USD' ? <span className="dim"> {r.currency}</span> : null}
                        {r.fxMissing ? <Excluded n={1} /> : null}
                      </span>
                    </div>
                    <div className="dr-figs">{figures(r)}</div>
                  </div>
                ))}
              </div>
            );
          })}
          <div className="day-group total">
            <div className="day-head">
              <span className="day">Total (rows shown)</span>
              <span className="day-sum">{fmtMoney(totals.payoutUsd)}</span>
            </div>
            <div className="day-row">
              <div className="dr-figs">
                {[
                  hiddenKeys.has('Searches') ? null : `${fmtInt(totals.searches)} searches`,
                  hiddenKeys.has('Monetized')
                    ? null
                    : `${fmtInt(totals.monetized)} monetized` +
                      (totals.searches > 0
                        ? ` (${((100 * totals.monetized) / totals.searches).toFixed(1)}%)`
                        : ''),
                  hiddenKeys.has('Clicks') ? null : `${fmtInt(totals.clicks)} clicks`,
                  hiddenKeys.has('CPC') || totals.clicks <= 0
                    ? null
                    : `CPC ${fmtMoney(totals.payoutUsd / totals.clicks)}`,
                  hiddenKeys.has('CTR') || totals.monetized <= 0
                    ? null
                    : `CTR ${fmtPct((100 * totals.clicks) / totals.monetized)}`,
                  hiddenKeys.has('Requests') ? null : `${fmtInt(totals.requests)} requests`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="stat-table">
            <thead>
              <tr>
                {shown.map((c) => (
                  <th key={c.key} className={c.head}>
                    {c.key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...byDay.entries()].map(([day, dayRows]) => {
                const dRev = dayRows.reduce((a, r) => a + r.payoutUsd, 0);
                const cost = dayCost.get(day);
                return [
                  <tr className="section" key={`s-${day}`}>
                    <td colSpan={shown.length}>
                      {mmdd(day)} — revenue {fmtMoney(dRev)}
                      {props.showCosts && cost != null
                        ? ` · attributed cost ${fmtMoney(cost)} · net after attributed cost ${fmtMoney(dRev - cost)} (whole day)`
                        : ''}
                      {day === props.partialDay ? ' · partial (today, still updating)' : ''}
                    </td>
                  </tr>,
                  ...dayRows.map((r, i) => (
                    <tr key={`${day}-${i}`}>
                      {shown.map((c) => (
                        <td key={c.key} className={tdClass(c, r)} title={c.title?.(r)}>
                          {c.cell(r)}
                        </td>
                      ))}
                    </tr>
                  )),
                ];
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="l" colSpan={footLead}>
                  Total (rows shown)
                </td>
                {shown
                  .filter((c) => c.foot !== undefined)
                  .map((c) => (
                    <td key={c.key} className={c.footClass}>
                      {c.foot}
                    </td>
                  ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {props.rows.length < props.total ? (
        <button className="ghost small" style={{ marginTop: 10 }} onClick={props.onMore}>
          Show more rows ({props.total - props.rows.length} remaining)
        </button>
      ) : null}
      <div className="table-note">
        Figures are as reported by each provider; coverage marked * is calculated from monetized ÷
        searches. Revenue, CPC and the totals are USD at the account's FX basis — a non-USD row with
        no rate yet is excluded and marked, never counted at face value. Costs are shown per day;
        under a country filter they come only from buy-side rows reported per country, and a day is
        left blank when any linked destination lacks them.
        {hidden.length ? ` ${hiddenColumnsNote(hidden)}` : ''}
      </div>
    </div>
  );
}
